import { Badge } from '@/components/ui/badge'
import * as Card from '@/components/ui/card'
import { IconButton } from '@/components/ui/icon-button'
import { DialogContainer } from '@components'
import createEmblaCarousel from 'embla-carousel-solid'
import { Play } from 'lucide-solid'
import {
  type Accessor,
  For,
  createEffect,
  onCleanup,
  onMount,
} from 'solid-js'
import { css } from 'styled-system/css'
import { Box, Flex } from 'styled-system/jsx'

export const SetupImageCarousel = (props: {
  openImage: Accessor<number | undefined>
  setIsOpen: (value: number | undefined) => void
  images: { title: string; src?: string | null | undefined }[]
}) => {
  // 1. Renamed to emblaApi for clarity
  const [emblaRef, emblaApi] = createEmblaCarousel(() => ({
    startIndex: props.openImage() ?? 0,
  }))

  // Add keyboard navigation
  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (props.openImage() == null) return // only active when dialog is open
      if (e.key === 'ArrowLeft') emblaApi()?.scrollPrev()
      if (e.key === 'ArrowRight') emblaApi()?.scrollNext()
    }

    window.addEventListener('keydown', handleKeyDown)
    onCleanup(() => window.removeEventListener('keydown', handleKeyDown))
  })

  // 2. Simplified createEffect: Instantly scroll when the prop changes
  createEffect(() => {
    const openIndex = props.openImage()
    if (openIndex != null && !Number.isNaN(openIndex)) {
      emblaApi()?.scrollTo(openIndex, true)
    }
  })

  return (
    <DialogContainer
      size="cover"
      padding={3}
      isOpen={props.openImage() != null && !Number.isNaN(props.openImage())}
      setIsOpen={() => props.setIsOpen(undefined)}
    >
      <Box
        position="relative"
        class={css({ overflow: 'hidden' })}
        ref={emblaRef}
      >
        <div
          class={css({
            display: 'flex',
            height: 'calc(100dvh - 110px)',
          })}
        >
          <For each={props.images}>
            {(image) => (
              <Box
                flex="0 0 100%"
                minWidth={0}
                position="relative" // 3. Added to keep the Badge attached to this specific slide
              >
                <Card.Root
                  style={{
                    'background-image': `url(${image.src})`,
                  }}
                  class={css({
                    height: '100%',
                    backgroundSize: 'contain', // 4. Changed from 'cover' to 'contain' for full-screen dialogs
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                  })}
                />
                <Box
                  position="absolute"
                  bottom={8}
                  width="100%"
                  textAlign="center"
                >
                  <Badge variant="subtle" size="lg">
                    {image.title}
                  </Badge>
                </Box>
              </Box>
            )}
          </For>
        </div>

        <Flex
          class={css({
            justifyContent: 'space-between',
            position: 'absolute',
            pointerEvents: 'none',
            top: 0,
            alignItems: 'center',
            width: '100%',
            height: '100%',
            left: 0,
            right: 0,
            px: 4, // 5. Added padding so arrows aren't flushed against the screen edge
          })}
        >
          <IconButton
            pointerEvents="auto"
            colorPalette="tscGreen"
            variant="plain"
            onClick={() => emblaApi()?.scrollPrev()}
          >
            <Play
              class={css({ transform: 'rotate(180deg)' })}
              fill="var(--colors-tsc-green-default)"
              size={48}
            />
          </IconButton>
          <IconButton
            pointerEvents="auto"
            colorPalette="tscGreen"
            variant="plain"
            onClick={() => emblaApi()?.scrollNext()}
          >
            <Play fill="var(--colors-tsc-green-default)" size={48} />
          </IconButton>
        </Flex>
      </Box>
    </DialogContainer>
  )
}
